/**
 * Reusable custom dropdown used in place of native <select>.
 *
 * Rationale: native selects inherit the OS chrome, which breaks visual
 * consistency across hosts and doesn't theme — particularly noticeable
 * for short lists like currency and product type. This helper renders a
 * themed button + listbox pair with keyboard support, click-outside
 * close, and a minimal event API that mirrors `select.addEventListener('change', ...)`.
 *
 * Intentionally NOT suitable for very long lists (hundreds of options).
 * The OS-native typeahead on long selects (e.g. country pickers) is a
 * usability win we don't want to reimplement here — keep those native.
 */

export interface CustomSelectOption {
  value: string;
  label: string;
  disabled?: boolean;
  /** Optional leading icon URL (e.g. flag PNG). Rendered at 18x12. */
  iconUrl?: string;
  /** Alt text for the icon. Falls back to the label. */
  iconAlt?: string;
}

export interface MountCustomSelectOptions {
  options: CustomSelectOption[];
  value: string;
  onChange: (newValue: string) => void;
  ariaLabel?: string;
  /** Label shown when no option matches the current value. */
  placeholder?: string;
  /**
   * Root used for outside-click detection. Defaults to the host's
   * closest ShadowRoot or the document. Pass explicitly if the host
   * lives in a custom container (e.g. a modal appended to document.body).
   */
  outsideRoot?: Document | ShadowRoot;
}

let styleSeq = 0;

/**
 * Replaces the contents of `host` with a custom dropdown and wires up
 * all event handlers. Idempotent — safe to call multiple times on the
 * same host when options/value change.
 */
export function mountCustomSelect(host: HTMLElement, opts: MountCustomSelectOptions): void {
  ensureCustomSelectStyles(host);

  const id = `ks-cs-${++styleSeq}`;
  const current = opts.options.find((o) => o.value === opts.value);
  const triggerLabel = current?.label ?? opts.placeholder ?? '';
  const triggerIcon = renderIcon(current?.iconUrl, current?.iconAlt || triggerLabel);

  host.innerHTML = `
    <div class="ks-cs" data-cs-id="${id}">
      <button type="button" class="ks-cs-trigger" aria-haspopup="listbox" aria-expanded="false"${
        opts.ariaLabel ? ` aria-label="${escapeAttr(opts.ariaLabel)}"` : ''
      }>
        ${triggerIcon}
        <span class="ks-cs-value">${escapeHtml(triggerLabel)}</span>
        <svg class="ks-cs-caret" viewBox="0 0 12 12" aria-hidden="true">
          <path d="M2 4l4 4 4-4" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
      <ul class="ks-cs-panel" role="listbox" tabindex="-1" hidden>
        ${opts.options
          .map(
            (o) => `
          <li
            role="option"
            class="ks-cs-option${o.value === opts.value ? ' is-selected' : ''}${o.disabled ? ' is-disabled' : ''}"
            data-value="${escapeAttr(o.value)}"
            aria-selected="${o.value === opts.value}"
            ${o.disabled ? 'aria-disabled="true"' : ''}
          >${renderIcon(o.iconUrl, o.iconAlt || o.label)}<span class="ks-cs-option-label">${escapeHtml(o.label)}</span></li>
        `
          )
          .join('')}
      </ul>
    </div>
  `;

  const wrap = host.querySelector('.ks-cs') as HTMLElement;
  const trigger = host.querySelector('.ks-cs-trigger') as HTMLButtonElement;
  const panel = host.querySelector('.ks-cs-panel') as HTMLElement;
  const valueEl = host.querySelector('.ks-cs-value') as HTMLElement;
  if (!wrap || !trigger || !panel) return;

  // Resolve the root we listen for outside-clicks on. Shadow DOM clicks
  // don't bubble to document, so use the ShadowRoot if we're inside one.
  const outsideRoot: Document | ShadowRoot =
    opts.outsideRoot || findOwnerRoot(host);

  let currentValue = opts.value;
  let isOpen = false;

  const options = () => Array.from(panel.querySelectorAll<HTMLElement>('.ks-cs-option'));

  const setActive = (index: number) => {
    const opts = options();
    opts.forEach((el, i) => {
      if (i === index) {
        el.classList.add('is-active');
        el.scrollIntoView({ block: 'nearest' });
      } else {
        el.classList.remove('is-active');
      }
    });
  };

  const open = () => {
    if (isOpen) return;
    isOpen = true;
    panel.hidden = false;
    trigger.setAttribute('aria-expanded', 'true');
    wrap.classList.add('is-open');
    const selectedIndex = options().findIndex((el) => el.dataset.value === currentValue);
    setActive(selectedIndex >= 0 ? selectedIndex : 0);
    // Defer so the current click doesn't immediately trigger the
    // outside-click handler.
    setTimeout(() => outsideRoot.addEventListener('click', onOutsideClick, true), 0);
    outsideRoot.addEventListener('keydown', onKeyDown, true);
  };

  const close = () => {
    if (!isOpen) return;
    isOpen = false;
    panel.hidden = true;
    trigger.setAttribute('aria-expanded', 'false');
    wrap.classList.remove('is-open');
    outsideRoot.removeEventListener('click', onOutsideClick, true);
    outsideRoot.removeEventListener('keydown', onKeyDown, true);
  };

  const commit = (value: string) => {
    if (value === currentValue) {
      close();
      return;
    }
    currentValue = value;
    const match = opts.options.find((o) => o.value === value);
    if (match && valueEl) {
      valueEl.textContent = match.label;
    }
    // Swap the trigger's leading icon to match the newly selected option.
    const triggerIconEl = trigger.querySelector('.ks-cs-icon') as HTMLElement | null;
    if (match?.iconUrl) {
      if (triggerIconEl) {
        (triggerIconEl as HTMLImageElement).src = match.iconUrl;
        (triggerIconEl as HTMLImageElement).alt = match.iconAlt || match.label;
      } else {
        trigger.insertAdjacentHTML('afterbegin', renderIcon(match.iconUrl, match.iconAlt || match.label));
      }
    } else if (triggerIconEl) {
      triggerIconEl.remove();
    }
    options().forEach((el) => {
      const selected = el.dataset.value === value;
      el.classList.toggle('is-selected', selected);
      el.setAttribute('aria-selected', String(selected));
    });
    close();
    opts.onChange(value);
  };

  const onOutsideClick = (e: Event) => {
    const target = e.target as Node | null;
    if (target && wrap.contains(target)) return;
    close();
  };

  const onKeyDown = (ev: Event) => {
    const e = ev as KeyboardEvent;
    if (!isOpen) return;
    const opts = options();
    const activeIndex = opts.findIndex((el) => el.classList.contains('is-active'));
    switch (e.key) {
      case 'Escape':
        e.preventDefault();
        close();
        trigger.focus();
        break;
      case 'ArrowDown':
        e.preventDefault();
        setActive(Math.min(activeIndex + 1, opts.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActive(Math.max(activeIndex - 1, 0));
        break;
      case 'Home':
        e.preventDefault();
        setActive(0);
        break;
      case 'End':
        e.preventDefault();
        setActive(opts.length - 1);
        break;
      case 'Enter':
      case ' ': {
        e.preventDefault();
        const active = opts[activeIndex];
        if (active && !active.classList.contains('is-disabled')) {
          commit(active.dataset.value || '');
        }
        break;
      }
    }
  };

  trigger.addEventListener('click', (e) => {
    e.stopPropagation();
    if (isOpen) close();
    else open();
  });

  panel.addEventListener('click', (e) => {
    const target = e.target as HTMLElement | null;
    const li = target?.closest('.ks-cs-option') as HTMLElement | null;
    if (!li || li.classList.contains('is-disabled')) return;
    commit(li.dataset.value || '');
  });
}

// ────────────────────────────────────────────────────────────────────────────
// Style injection — once per owning root (document or shadow root).
// ────────────────────────────────────────────────────────────────────────────

const STYLE_MARKER = 'data-ks-cs-styles';

function ensureCustomSelectStyles(host: HTMLElement): void {
  const root = findOwnerRoot(host);
  // Document or shadow root — both support getElementById/querySelector on
  // descendants via the same API surface.
  const scope: Document | ShadowRoot = root;
  const existing = (scope as Document).querySelector?.(`style[${STYLE_MARKER}]`);
  if (existing) return;

  const style = document.createElement('style');
  style.setAttribute(STYLE_MARKER, 'true');
  style.textContent = CUSTOM_SELECT_STYLES;

  if (scope instanceof ShadowRoot) {
    scope.appendChild(style);
  } else {
    document.head.appendChild(style);
  }
}

function findOwnerRoot(el: HTMLElement): Document | ShadowRoot {
  const root = el.getRootNode();
  if (root instanceof ShadowRoot) return root;
  return document;
}

function renderIcon(url: string | undefined, alt: string): string {
  if (!url) return '';
  return `<img class="ks-cs-icon" src="${escapeAttr(url)}" alt="${escapeAttr(alt)}" loading="lazy" />`;
}

function escapeHtml(s: string): string {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function escapeAttr(s: string): string {
  return escapeHtml(s);
}

const CUSTOM_SELECT_STYLES = `
.ks-cs {
  position: relative;
  display: inline-block;
  min-width: 80px;
  font: inherit;
}

.ks-cs-trigger {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 12px;
  font: inherit;
  color: inherit;
  background: var(--ks-cs-bg, transparent);
  border: 1px solid var(--ks-cs-border, currentColor);
  border-radius: var(--ks-cs-radius, 8px);
  cursor: pointer;
  line-height: 1.2;
  transition: border-color 120ms ease, background 120ms ease;
}

.ks-cs-trigger:hover,
.ks-cs-trigger:focus-visible {
  border-color: var(--ks-cs-border-hover, var(--ks-primary, currentColor));
  outline: none;
}

.ks-cs-value {
  flex: 1;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ks-cs-caret {
  width: 12px;
  height: 12px;
  flex-shrink: 0;
  transition: transform 150ms ease;
}

.ks-cs-icon {
  width: 18px;
  height: 12px;
  flex-shrink: 0;
  border-radius: 2px;
  object-fit: cover;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.06);
}

.ks-cs.is-open .ks-cs-caret {
  transform: rotate(180deg);
}

.ks-cs-panel {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  min-width: 100%;
  max-height: 280px;
  overflow-y: auto;
  margin: 0;
  padding: 4px;
  list-style: none;
  background: var(--ks-cs-panel-bg, #ffffff);
  color: var(--ks-cs-panel-ink, #111827);
  border: 1px solid var(--ks-cs-panel-border, rgba(0, 0, 0, 0.08));
  border-radius: var(--ks-cs-radius, 8px);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.12);
  z-index: 1000;
}

.ks-cs-option {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: calc(var(--ks-cs-radius, 8px) - 4px);
  cursor: pointer;
  font: inherit;
  white-space: nowrap;
}

.ks-cs-option-label {
  flex: 1;
}

.ks-cs-option:hover,
.ks-cs-option.is-active {
  background: var(--ks-cs-option-hover-bg, rgba(0, 0, 0, 0.05));
}

.ks-cs-option.is-selected {
  font-weight: 600;
  color: var(--ks-cs-option-selected-ink, var(--ks-primary, inherit));
}

.ks-cs-option.is-disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
`;
